import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator, Plus } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function ResistanceCalculator() {
  const [resistors, setResistors] = useState<string[]>(["", ""]);
  const [result, setResult] = useState<number | null>(null);

  useLanguageAwareSEO([
    (language) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Physics", url: "https://calclab.app/physics" },
      { name: "Resistance Calculator", url: "https://calclab.app/calculator/resistance" }
    ], language),
    (language) => calculatorToolSchema(
      "Series/Parallel Resistance Calculator",
      "Calculate equivalent resistance for series and parallel circuits",
      "https://calclab.app/calculator/resistance",
      language
    )
  ]);

  const addResistor = () => {
    setResistors([...resistors, ""]);
  };

  const updateResistor = (index: number, value: string) => {
    const newResistors = [...resistors];
    newResistors[index] = value;
    setResistors(newResistors);
  };

  const removeResistor = (index: number) => {
    if (resistors.length > 2) {
      const newResistors = resistors.filter((_, i) => i !== index);
      setResistors(newResistors);
    }
  };

  const calculateSeries = () => {
    const values = resistors.map(r => parseFloat(r)).filter(v => !isNaN(v));
    
    if (values.length < 2) {
      toast.error("Please enter at least 2 resistor values");
      return;
    }

    const total = values.reduce((sum, r) => sum + r, 0);
    setResult(total);
    toast.success("Calculation complete!");
  };

  const calculateParallel = () => {
    const values = resistors.map(r => parseFloat(r)).filter(v => !isNaN(v) && v > 0);
    
    if (values.length < 2) {
      toast.error("Please enter at least 2 resistor values");
      return;
    }

    const reciprocalSum = values.reduce((sum, r) => sum + (1 / r), 0);
    const total = 1 / reciprocalSum;
    setResult(total);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setResistors(["", ""]);
    setResult(null);
  };

  return (
    <PageLayout calculatorName="Series/Parallel Resistance Calculator" calculatorPath="/calculator/resistance">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Resistance Formulas</CardTitle>
            <CardDescription>
              Series: R_total = R₁ + R₂ + ... | Parallel: 1/R_total = 1/R₁ + 1/R₂ + ...
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs defaultValue="series" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="series">Series</TabsTrigger>
                <TabsTrigger value="parallel">Parallel</TabsTrigger>
              </TabsList>

              <TabsContent value="series" className="space-y-4 mt-4">
                <div className="space-y-4">
                  {resistors.map((value, index) => (
                    <div key={index} className="flex gap-2 items-end">
                      <div className="flex-1 space-y-2">
                        <Label htmlFor={`resistor-${index}`}>Resistor {index + 1} (Ω)</Label>
                        <Input
                          id={`resistor-${index}`}
                          type="number"
                          step="0.01"
                          placeholder="Enter resistance"
                          value={value}
                          onChange={(e) => updateResistor(index, e.target.value)}
                        />
                      </div>
                      {resistors.length > 2 && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => removeResistor(index)}
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  ))}
                  
                  <Button onClick={addResistor} variant="outline" className="w-full">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Resistor
                  </Button>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateSeries} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate Series
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="parallel" className="space-y-4 mt-4">
                <div className="space-y-4">
                  {resistors.map((value, index) => (
                    <div key={index} className="flex gap-2 items-end">
                      <div className="flex-1 space-y-2">
                        <Label htmlFor={`resistor-p-${index}`}>Resistor {index + 1} (Ω)</Label>
                        <Input
                          id={`resistor-p-${index}`}
                          type="number"
                          step="0.01"
                          placeholder="Enter resistance"
                          value={value}
                          onChange={(e) => updateResistor(index, e.target.value)}
                        />
                      </div>
                      {resistors.length > 2 && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => removeResistor(index)}
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  ))}
                  
                  <Button onClick={addResistor} variant="outline" className="w-full">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Resistor
                  </Button>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateParallel} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate Parallel
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>
              </TabsContent>
            </Tabs>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toFixed(2)} Ω
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Equivalent Resistance
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["resistance"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Choose between Series or Parallel configuration</p>
                <p>2. Enter resistance values for each resistor</p>
                <p>3. Add more resistors using the "Add Resistor" button</p>
                <p>4. Click Calculate to get equivalent resistance</p>
                <p className="pt-2 font-medium text-foreground">
                  Series: 10Ω + 20Ω = 30Ω | Parallel: 10Ω || 10Ω = 5Ω
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
